import { useEffect } from 'react';

import { Navigate, Outlet, useNavigate, useParams } from 'react-router-dom';

import { Box, Flex, Image } from '@chakra-ui/react';

import Navbar from 'app/components/Navbar';
import FullscreenLoading from 'app/components/UI/FullscreenLoading';
import CommandPalette from 'app/components/CommandPalette/CommandPalette';

import { authPaths } from 'app/constants/routes';

import useAuthContext from 'app/hooks/useAuthContext';
import useFetchCompany from 'app/hooks/useFetchCompany';
import { Footer, Sidebar, useToast } from '@aslanpay/portal-common-lib';
import useCommandPalette from 'app/hooks/useCommandPalette';

import { useManagingCompanyStore, useNavigationStore } from 'app/store';

import fullLogoWhite from 'app/assets/svg/fullLogoWhite.svg';

import BackToCompanies from './BackToCompanies';
import { useSidebarNav } from './navSections';
import { AuthenticatedLayoutProps } from './types';

const VERSION = import.meta.env.VITE_APPLICATION_VERSION;

function AuthenticatedLayout({ view }: AuthenticatedLayoutProps) {
  const { user } = useAuthContext();

  const toast = useToast();

  const navigate = useNavigate();
  const { companyId } = useParams();

  const setView = useNavigationStore((state) => state.setView);
  const setCompany = useManagingCompanyStore((state) => state.setCompany);

  const { data: company, isInitialLoading: isLoadingCompany } = useFetchCompany(
    companyId || user?.companyId
  );

  const { isOpen: isCommandPaletteOpen, onClose: onCloseCommandPalette } = useCommandPalette();

  const { sections, activeId } = useSidebarNav(view);

  const isSwitchingToCompany = view === 'company' && useNavigationStore.getState().view !== view;

  useEffect(() => {
    // Show toast showing the managing company when we navigate inside a company
    if (isSwitchingToCompany && company) {
      toast({
        position: 'top-right',
        status: 'success',
        title: `Now managing "${company?.name}"`,
      });
    }
  }, [isSwitchingToCompany, company, toast]);

  useEffect(() => {
    setCompany(company);
  }, [company, setCompany]);

  useEffect(() => {
    if (view === 'company') {
      if (company) setView(view);
    } else {
      setView(view);
    }
  }, [view, company, setView]);

  if (!user) {
    return <Navigate to={authPaths.authentication} />;
  }

  if (isLoadingCompany) return <FullscreenLoading />;

  const companyInfo =
    view === 'company'
      ? { label: 'Company', name: company?.name ?? '' }
      : { name: 'Aslan Backoffice' };

  return (
    <Flex h="100vh" direction={{ base: 'column', lg: 'row' }} overflow="hidden">
      <Sidebar
        sections={sections}
        company={companyInfo}
        activeId={activeId}
        onNavigate={(path) => navigate(path)}
        variant="dark"
        headerContent={
          view === 'company' && company ? (
            <BackToCompanies onNavigate={(path) => navigate(path)} />
          ) : undefined
        }
        logo={<Image src={fullLogoWhite} alt="Aslan" width="100px" />}
      />
      <CommandPalette isOpen={isCommandPaletteOpen} onClose={onCloseCommandPalette} />
      <Box data-scroll-container flex="1" overflowY="auto" display="flex" flexDir="column">
        <Navbar />
        <Flex flex="1">
          <Outlet />
        </Flex>
        <Footer version={VERSION} />
      </Box>
    </Flex>
  );
}

export default AuthenticatedLayout;
